import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import SearchableDropdown from '../../../components/common/SearchableDropdown';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const DeliveryOrderExtensionForm = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const [formData, setFormData] = useState({
    delivery_order_id: '',
    delivery_order_no: '',
    delivery_order_date: '',
    date_of_extension: '',
    extended_up_to: ''
  });

  const [loading, setLoading] = useState(false);
  const [deliveryOrders, setDeliveryOrders] = useState([]);
  const [closingDate, setClosingDate] = useState(null);
  const [extensionCount, setExtensionCount] = useState(null);
  const [currentExtensionNumber, setCurrentExtensionNumber] = useState(0);
  const [errors, setErrors] = useState({});

  const navigate = useNavigate();

  const getMaxDate = () => new Date().toISOString().split('T')[0]; // YYYY-MM-DD

  const preventDateTyping = (e) => {
    if (e.key !== 'Tab' && e.key !== 'Escape') e.preventDefault();
  };

  // Max for Date of Extension: no future dates, and not after closing date
  const getDateOfExtensionMax = () => {
    const today = getMaxDate();
    if (!closingDate) return today;
    const closing = formatDateForInput(closingDate);
    return closing && closing < today ? closing : today;
  };

  // Helper function to format date for input field (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      // Handle different date formats from database
      let date;
      if (typeof dateString === 'string') {
        // If already in YYYY-MM-DD format, return as is
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
          return dateString;
        }
        // Parse the date string
        date = new Date(dateString);
      } else {
        date = dateString;
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return '';
      }
      
      // Format as YYYY-MM-DD (timezone-safe)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return '';
    }
  };

  // Fetch delivery orders
  useEffect(() => {
    const fetchDeliveryOrders = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-do`, {
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          setDeliveryOrders(data.data || []);
        }
      } catch (err) {
        console.error('Error fetching delivery orders:', err);
        toast.error('Failed to load delivery orders');
      }
    };
    fetchDeliveryOrders();
  }, []);

  // Fetch extension data for edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const fetchExtensionForEdit = async () => {
        try {
          const res = await fetch(`${API_BASE_URL}/api/sales/delivery-order-extension-get?extension_id=${id}`, {
            credentials: 'include'
          });
          if (res.ok) {
            const data = await res.json();
            const extension = data.data?.[0];
            if (extension) {
              setFormData({
                delivery_order_id: extension.delivery_order_id,
                delivery_order_no: extension.do_number || '',
                delivery_order_date: formatDateForInput(extension.do_date) || '',
                date_of_extension: formatDateForInput(extension.date_of_extension) || '',
                extended_up_to: formatDateForInput(extension.extended_up_to) || ''
              });
              
              // Set current extension_number for validation
              setCurrentExtensionNumber(extension.extension_number || 0);
              
              // Fetch closing date and extension count
              if (extension.delivery_order_id) {
                const doRes = await fetch(
                  `${API_BASE_URL}/api/sales/delivery-order-get/${extension.delivery_order_id}`,
                  { credentials: 'include' }
                );
                
                if (doRes.ok) {
                  const doData = await doRes.json();
                  const deliveryOrder = doData.data;
                  
                  if (deliveryOrder.sales_contract_id) {
                    const contractRes = await fetch(
                      `${API_BASE_URL}/api/sales/sale-contract/${deliveryOrder.sales_contract_id}`,
                      { credentials: 'include' }
                    );
                    
                    if (contractRes.ok) {
                      const contractData = await contractRes.json();
                      if (contractData.data?.closing_date) {
                        setClosingDate(contractData.data.closing_date);
                      }
                    }
                  }
                  
                  // Fetch extension count
                  const extensionRes = await fetch(
                    `${API_BASE_URL}/api/sales/delivery-order-extension-get?delivery_order_id=${extension.delivery_order_id}`,
                    { credentials: 'include' }
                  );
                  
                  if (extensionRes.ok) {
                    const extensionData = await extensionRes.json();
                    const count = extensionData.data?.length || 0;
                    setExtensionCount(count);
                  }
                }
              }
            }
          }
        } catch (err) {
          console.error('Error fetching extension for edit:', err);
          toast.error('Failed to load extension data');
        }
      };
      fetchExtensionForEdit();
    }
  }, [isEditMode, id]);

  // Fetch existing extensions and sales contract details when delivery order is selected
  useEffect(() => {
    if (formData.delivery_order_id) {
      const fetchExtensionData = async () => {
        try {
          // Fetch existing extensions for this delivery order
          const extensionRes = await fetch(
            `${API_BASE_URL}/api/sales/delivery-order-extension-get?delivery_order_id=${formData.delivery_order_id}`,
            { credentials: 'include' }
          );
          
          if (extensionRes.ok) {
            const extensionData = await extensionRes.json();
            const count = extensionData.data?.length || 0;
            setExtensionCount(count);
          }

          // Fetch delivery order details to get sales contract and closing date
          const doRes = await fetch(
            `${API_BASE_URL}/api/sales/delivery-order-get/${formData.delivery_order_id}`,
            { credentials: 'include' }
          );
          
          if (doRes.ok) {
            const doData = await doRes.json();
            const deliveryOrder = doData.data;
            
            // Fetch sales contract to get closing date
            if (deliveryOrder.sales_contract_id) {
              const contractRes = await fetch(
                `${API_BASE_URL}/api/sales/sale-contract/${deliveryOrder.sales_contract_id}`,
                { credentials: 'include' }
              );
              
              if (contractRes.ok) {
                const contractData = await contractRes.json();
                if (contractData.data?.closing_date) {
                  setClosingDate(contractData.data.closing_date);
                }
              }
            }
          }
        } catch (err) {
          console.error('Error fetching extension data:', err);
        }
      };
      fetchExtensionData();
    } else {
      setExtensionCount(null);
      setClosingDate(null);
    }
  }, [formData.delivery_order_id]);

  // Auto-fill delivery order date when delivery order is selected
  useEffect(() => {
    if (formData.delivery_order_id && deliveryOrders.length > 0) {
      const selectedDO = deliveryOrders.find(
        doItem => doItem.do_id.toString() === formData.delivery_order_id.toString()
      );
      if (selectedDO) {
        setFormData(prev => ({
          ...prev,
          delivery_order_date: formatDateForInput(selectedDO.do_date) || '',
          delivery_order_no: selectedDO.do_number || ''
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        delivery_order_date: '',
        delivery_order_no: ''
      }));
    }
  }, [formData.delivery_order_id, deliveryOrders]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.delivery_order_id) {
      newErrors.delivery_order_id = 'Delivery Order No is required';
    }

    if (!formData.date_of_extension) {
      newErrors.date_of_extension = 'Date of Extension is required';
    }

    if (!formData.extended_up_to) {
      newErrors.extended_up_to = 'Extended Up To is required';
    }

    // Validate extension count (max 3) - exclude current extension in edit mode
    if (isEditMode) {
      // In edit mode, we're updating existing extension, so check if count would exceed 3
      // The count already includes this extension, so we don't need to add 1
      if (extensionCount > 3) {
        newErrors.extension_count = 'Maximum 3 extensions are allowed for this delivery order';
      }
    } else {
      // In create mode, if extensions already exist, don't allow creating new one
      if (extensionCount > 0) {
        newErrors.extension_count = `This Delivery Order already has ${extensionCount} extension(s) created. You cannot create a new extension for this delivery order.`;
      }
      // Also check if adding one more would exceed 3
      if (extensionCount >= 3) {
        newErrors.extension_count = 'Maximum 3 extensions are allowed for this delivery order';
      }
    }

    // Validate date of extension should not be a future date
    if (formData.date_of_extension) {
      const maxDate = getMaxDate();
      if (formData.date_of_extension > maxDate) {
        newErrors.date_of_extension = 'Future dates are not allowed for Date of Extension';
      }
    }

    // Validate date of extension should not be after closing date
    if (closingDate && formData.date_of_extension) {
      const extensionDate = new Date(formData.date_of_extension);
      const closing = new Date(closingDate);
      if (extensionDate > closing) {
        newErrors.date_of_extension = 'Date of Extension cannot be after the closing date';
      }
    }

    // Validate extended_up_to should not be after closing date
    if (closingDate && formData.extended_up_to) {
      const extendedDate = new Date(formData.extended_up_to);
      const closing = new Date(closingDate);
      if (extendedDate > closing) {
        newErrors.extended_up_to = 'Extended Up To cannot be after the closing date';
      }
    }

    // Validate extended_up_to should be after date_of_extension
    if (formData.date_of_extension && formData.extended_up_to) {
      const extensionDate = new Date(formData.date_of_extension);
      const extendedDate = new Date(formData.extended_up_to);
      if (extendedDate <= extensionDate) {
        newErrors.extended_up_to = 'Extended Up To must be after Date of Extension';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the validation errors');
      return;
    }

    setLoading(true);
    try {
      const url = isEditMode 
        ? `${API_BASE_URL}/api/sales/delivery-order-extension-put/${id}`
        : `${API_BASE_URL}/api/sales/delivery-order-extension-post`;
      
      const method = isEditMode ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          delivery_order_id: formData.delivery_order_id,
          date_of_extension: formData.date_of_extension,
          extended_up_to: formData.extended_up_to
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(isEditMode 
          ? 'Delivery Order Extension updated successfully' 
          : 'Delivery Order Extension created successfully');
        setTimeout(() => {
          navigate('/delivery-order-extension');
        }, 1500);
      } else {
        toast.error(data.error || (isEditMode 
          ? 'Failed to update delivery order extension' 
          : 'Failed to create delivery order extension'));
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('An error occurred while submitting the form');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (name, value) => {
    if (name === 'date_of_extension' && value) {
      const maxDate = getDateOfExtensionMax();
      if (value > maxDate) {
        setErrors(prev => ({
          ...prev,
          date_of_extension: 'Future dates are not allowed for Date of Extension'
        }));
        return;
      }
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-4 py-1.5">
        <h2 className="text-lg font-bold mb-0 text-left">
          {isEditMode ? 'Edit Delivery Order Extension' : 'Delivery Order Extension'}
        </h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#2691cc] border border-[#2691cc] px-2 py-0.5 rounded text-xs font-medium hover:bg-[#eaf6fd]"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-0">
        <div className="px-4 pt-4 pb-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Delivery Order No */}
            <div>
              <label className="block text-xs font-medium mb-1">
                Delivery Order No <span className="text-red-500">*</span>
              </label>
              <SearchableDropdown
                options={deliveryOrders}
                value={formData.delivery_order_id}
                onChange={(value) => handleChange('delivery_order_id', value)}
                placeholder="Select Delivery Order No"
                displayKey="do_number"
                valueKey="do_id"
                required
                disabled={isEditMode}
              />
              {errors.delivery_order_id && (
                <p className="text-red-500 text-xs mt-1">{errors.delivery_order_id}</p>
              )}
              {isEditMode && (
                <p className="text-xs text-gray-500 mt-1">Delivery Order cannot be changed in edit mode</p>
              )}
            </div>

            {/* Delivery Order Date (Auto-filled) */}
            <div>
              <label className="block text-xs font-medium mb-1">
                Delivery Order Date
              </label>
              <input
                type="date"
                value={formData.delivery_order_date}
                readOnly
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-gray-100 focus:outline-none rounded-none cursor-not-allowed"
              />
            </div>

            {/* Date of Extension */}
            <div>
              <label className="block text-xs font-medium mb-1">
                Date of Extension <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.date_of_extension}
                onChange={(e) => handleChange('date_of_extension', e.target.value)}
                onKeyDown={preventDateTyping}
                max={getDateOfExtensionMax()}
                className={`w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none cursor-pointer ${
                  errors.date_of_extension ? 'border-red-500' : ''
                }`}
                title="Select date from calendar only (future dates not allowed)"
                required
              />
              {errors.date_of_extension && (
                <p className="text-red-500 text-xs mt-1">{errors.date_of_extension}</p>
              )}
            </div>

            {/* Extended Up To */}
            <div>
              <label className="block text-xs font-medium mb-1">
                Extended Up To <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.extended_up_to}
                onChange={(e) => handleChange('extended_up_to', e.target.value)}
                min={formData.date_of_extension || undefined}
                max={closingDate || undefined}
                className={`w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none ${
                  errors.extended_up_to ? 'border-red-500' : ''
                }`}
                required
              />
              {errors.extended_up_to && (
                <p className="text-red-500 text-xs mt-1">{errors.extended_up_to}</p>
              )}
            </div>

            {/* Extension Count Error - Show when creating new extension and extensions already exist */}
            {!isEditMode && extensionCount > 0 && (
              <div className="md:col-span-2">
                <div className="bg-red-50 border border-red-300 rounded p-3">
                  <p className="text-sm text-red-900 font-semibold mb-2">
                    <strong>⚠️ Extension Already Exists</strong>
                  </p>
                  <p className="text-xs text-red-800 mb-2">
                    The Delivery Order <strong>"{formData.delivery_order_no}"</strong> already has <strong>{extensionCount} extension(s)</strong> created.
                  </p>
                  <p className="text-xs text-red-700 font-medium">
                    💡 <strong>What you can do:</strong>
                  </p>
                  <ul className="text-xs text-red-700 mt-1 ml-4 list-disc">
                    <li>Select a different Delivery Order to create a new extension</li>
                    <li>Go to the Extension List and edit the existing extension(s) for this Delivery Order</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Extension Count Info - Show in edit mode */}
            {isEditMode && formData.delivery_order_id && currentExtensionNumber > 0 && (
              <div className="md:col-span-2">
                <div className="bg-blue-50 border border-blue-200 rounded p-2">
                  <p className="text-xs text-blue-800">
                    <strong>Info:</strong> This delivery order has {currentExtensionNumber} extension.
                  </p>
                </div>
              </div>
            )}

            {/* Closing Date Warning */}
            {closingDate && (
              <div className="md:col-span-2">
                <div className="bg-blue-50 border border-blue-200 rounded p-2">
                  <p className="text-xs text-blue-800">
                    <strong>Closing Date:</strong> {new Date(closingDate).toLocaleDateString('en-GB')}
                  </p>
                </div>
              </div>
            )}

            {/* Extension Count Error */}
            {errors.extension_count && (
              <div className="md:col-span-2">
                <div className="bg-red-50 border border-red-200 rounded p-2">
                  <p className="text-xs text-red-800">{errors.extension_count}</p>
                </div>
              </div>
            )}

            {/* Warning if extension_number is already 3 in edit mode */}
            {isEditMode && currentExtensionNumber >= 3 && (
              <div className="md:col-span-2">
                <div className="bg-red-50 border border-red-200 rounded p-2">
                  <p className="text-xs text-red-800">
                    <strong>Warning:</strong> Extension is not allowed. You already have extension 3 times. Maximum 3 extensions are allowed. You cannot update this extension.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="px-4 pt-2 pb-4">
          <button
            type="submit"
            className="w-auto px-4 py-1.5 text-sm bg-[#2691cc] text-white font-semibold tracking-wide rounded border border-[#2691cc] hover:bg-[#0e8ac2] transition disabled:opacity-60"
            style={{ minWidth: '100px' }}
            disabled={loading || (isEditMode && currentExtensionNumber >= 3) || (!isEditMode && extensionCount > 0)}
          >
            {loading ? 'Saving...' : isEditMode ? 'Update Extension' : 'Save Extension'}
          </button>
        </div>
      </form>

      <ToastContainer 
        position="top-right" 
        autoClose={3000} 
        hideProgressBar={false} 
        newestOnTop 
        closeOnClick 
        pauseOnFocusLoss 
        draggable 
        pauseOnHover 
      />
    </div>
  );
};

export default DeliveryOrderExtensionForm;

